import { db } from '../../src/firebase.js';
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp } from 'firebase/firestore';

export default class MathBrainTrainer {
  constructor(containerId = 'app-content') {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('Container not found');
      return;
    }

    this.currentMode = '';
    this.currentLevel = 1;
    this.score = 0;
    this.timeLeft = 60;
    this.timerInterval = null;
    this.questionsAnswered = 0;
    this.currentProblem = { a: 0, b: 0, answer: 0, operator: '' };
    this.unsubscribe = null;

    this.renderMenu();
  }

  generateProblem() {
    const range = this.currentLevel * 10;
    const a = Math.floor(Math.random() * range) + 1;
    const b = Math.floor(Math.random() * range) + 1;

    if (this.currentMode === 'addition') {
      this.currentProblem = { a, b, answer: a + b, operator: '+' };
    } else {
      this.currentProblem = { a, b, answer: a * b, operator: '×' };
    }
    return `${a} ${this.currentProblem.operator} ${b}`;
  }

  startTimer() {
    this.timeLeft = 60;
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.timerInterval = setInterval(() => {
      this.timeLeft--;
      const timerEl = document.getElementById('trainer-timer');
      if (timerEl) timerEl.textContent = this.timeLeft;

      if (this.timeLeft <= 10 && timerEl) timerEl.classList.add('text-red-500', 'animate-pulse');
      if (this.timeLeft <= 0) this.endGame();
    }, 1000);
  }

  submitAnswer() {
    const input = document.getElementById('trainer-answer');
    const feedback = document.getElementById('trainer-feedback');
    const userAnswer = parseInt(input?.value.trim());

    if (isNaN(userAnswer)) {
      if (feedback) {
        feedback.textContent = 'Enter a number!';
        feedback.className = 'text-amber-800 text-xl py-3';
      }
      return;
    }

    this.questionsAnswered++;

    const isCorrect = userAnswer === this.currentProblem.answer;
    if (isCorrect) {
      this.score += 10 * this.currentLevel;
      const scoreEl = document.getElementById('trainer-score');
      if (scoreEl) scoreEl.textContent = this.score;
    }

    if (feedback) {
      feedback.textContent = isCorrect 
        ? `✓ +${10 * this.currentLevel} points!` 
        : `✗ Wrong! Correct: ${this.currentProblem.answer}`;
      feedback.className = isCorrect ? 'text-green-400' : 'text-red-400';
      feedback.classList.remove('hidden');
    }

    setTimeout(() => {
      if (feedback) feedback.classList.add('hidden');
      if (input) input.value = '';
      const problemEl = document.getElementById('trainer-problem');
      if (problemEl) problemEl.textContent = this.generateProblem();
    }, isCorrect ? 500 : 1200);
  }

  renderMenu() {
    this.container.innerHTML = `
      <div class="game-container max-w-2xl mx-auto rounded-3xl p-10 shadow-xl text-center bg-white border border-zinc-200 text-zinc-900">
        <h1 class="text-5xl mb-4 text-blue-900 leading-tight">MATH<br>BRAIN<br>TRAINER</h1>
        <p class="text-xl mb-10 text-zinc-600">Beat the clock • Train your brain • Global leaderboard</p>
        
        <div class="grid grid-cols-2 gap-6 mb-10">
          <button onclick="window.mathTrainer.startAddition()" class="bg-white hover:bg-yellow-300 text-blue-950 py-10 text-2xl font-bold rounded-2xl transition transform hover:scale-105">➕ ADDITION</button>
          <button onclick="window.mathTrainer.startMultiplication()" class="bg-white hover:bg-yellow-300 text-blue-950 py-10 text-2xl font-bold rounded-2xl transition transform hover:scale-105">✖️ MULTIPLICATION</button>
        </div>

        <button onclick="window.mathTrainer.showLeaderboard()" class="w-full border-4 border-blue-200 hover:bg-blue-50 text-blue-900 py-5 text-xl rounded-2xl transition font-bold">🏆 GLOBAL LEADERBOARD</button>
      </div>
    `;
  }

  startAddition() { this.startMode('addition'); }
  startMultiplication() { this.startMode('multiplication'); }

  startMode(mode) {
    this.currentMode = mode;
    let html = `
      <div class="game-container max-w-2xl mx-auto rounded-3xl p-10 shadow-xl text-center bg-white border border-zinc-200 text-zinc-900">
        <h2 class="text-4xl mb-8">${mode === 'addition' ? '➕ ADDITION' : '✖️ MULTIPLICATION'}</h2>
        <p class="mb-8 text-zinc-600">Choose difficulty level</p>
        <div class="grid grid-cols-5 gap-4">`;

    for (let i = 1; i <= 5; i++) {
      html += `<button onclick="window.mathTrainer.startGame(${i})" class="py-8 text-3xl font-bold bg-blue-50 border border-blue-100 hover:bg-amber-200 hover:border-amber-300 hover:text-blue-950 rounded-2xl transition">${i}</button>`;
    }

    html += `</div><button onclick="window.mathTrainer.renderMenu()" class="mt-10 underline text-sm text-zinc-600">← Back to menu</button></div>`;
    this.container.innerHTML = html;
  }

  startGame(level) {
    this.currentLevel = level;
    this.score = 0;
    this.questionsAnswered = 0;

    this.container.innerHTML = `
      <div class="game-container max-w-2xl mx-auto rounded-3xl p-8 shadow-xl bg-white border border-zinc-200 text-zinc-900">
        <div class="flex justify-between items-center mb-8">
          <div class="flex items-center gap-4">
            <span class="text-5xl">${this.currentMode === 'addition' ? '➕' : '✖️'}</span>
            <div>
              <div class="text-xl font-bold">LEVEL ${level}</div>
              <div id="trainer-score" class="text-4xl font-bold text-amber-600">0</div>
            </div>
          </div>
          <div class="text-center">
            <div class="text-xs tracking-widest text-zinc-500">TIME LEFT</div>
            <div id="trainer-timer" class="text-7xl font-bold text-red-500 tabular-nums">60</div>
          </div>
        </div>

        <div class="bg-zinc-50 border border-zinc-100 rounded-3xl p-12 text-center mb-8">
          <div id="trainer-problem" class="text-7xl font-bold mb-10 text-blue-950"></div>
          <input id="trainer-answer" type="text" 
                 class="w-full text-6xl text-center bg-white text-zinc-900 py-8 rounded-3xl outline-none focus:ring-4 focus:ring-yellow-300"
                 placeholder="?" autocomplete="off" onkeydown="if(event.key==='Enter') window.mathTrainer.submitAnswer()">
          <button onclick="window.mathTrainer.submitAnswer()" 
                  class="mt-8 w-full bg-yellow-300 hover:bg-white text-blue-950 py-6 rounded-3xl text-2xl font-bold">SUBMIT ANSWER</button>
        </div>

        <div id="trainer-feedback" class="hidden text-2xl font-bold py-4 rounded-2xl"></div>
        <button onclick="window.mathTrainer.endGameEarly()" class="text-red-600 underline text-sm mt-4">End game early</button>
      </div>
    `;

    const problemEl = document.getElementById('trainer-problem');
    if (problemEl) problemEl.textContent = this.generateProblem();

    this.startTimer();

    // Focus input
    setTimeout(() => document.getElementById('trainer-answer')?.focus(), 100);
  }

  endGameEarly() {
    if (confirm('End the game now?')) this.endGame();
  }

  endGame() {
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.container.innerHTML = `
      <div class="game-container max-w-2xl mx-auto rounded-3xl p-12 text-center bg-white border border-zinc-200 text-zinc-900 shadow-xl">
        <h2 class="text-5xl mb-4 text-blue-950">TIME'S UP!</h2>
        <p class="text-zinc-600 mb-6">Your final score</p>
        <div class="text-8xl font-bold text-amber-600 mb-10">${this.score}</div>
        
        <input id="trainer-name" type="text" value="Player" 
               class="w-full text-center text-3xl bg-zinc-50 border border-zinc-200 py-5 rounded-3xl mb-8 outline-none focus:ring-2 focus:ring-blue-400">
        
        <button onclick="window.mathTrainer.saveScore()" 
                class="w-full bg-yellow-300 hover:bg-white text-blue-950 py-7 rounded-3xl text-2xl font-bold mb-6">
          SAVE TO GLOBAL LEADERBOARD
        </button>
        <button onclick="window.mathTrainer.renderMenu()" class="underline text-blue-700">Play again</button>
      </div>
    `;
  }

  async saveScore() {
    const name = document.getElementById('trainer-name').value.trim() || 'Anonymous';

    try {
      await addDoc(collection(db, 'mathBrainLeaderboard'), {
        name,
        score: this.score,
        mode: this.currentMode,
        level: this.currentLevel,
        timestamp: serverTimestamp()
      });
      alert('Score saved successfully!');
      this.showLeaderboard();
    } catch (err) {
      console.error(err);
      alert('Failed to save score. Check your Firestore rules.');
    }
  }

  showLeaderboard() {
    this.container.innerHTML = `
      <div class="max-w-2xl mx-auto bg-white border border-zinc-200 rounded-3xl p-8 min-h-[500px] text-zinc-900 shadow-xl">
        <div class="flex justify-between mb-8">
          <h2 class="text-4xl font-bold text-blue-950">🏆 GLOBAL LEADERBOARD</h2>
          <button onclick="window.mathTrainer.renderMenu()" class="text-4xl leading-none text-zinc-500 hover:text-zinc-900" aria-label="Close">✕</button>
        </div>
        <div id="trainer-leaderboard-list" class="space-y-4"></div>
      </div>
    `;

    const q = query(collection(db, 'mathBrainLeaderboard'), orderBy('score', 'desc'), limit(15));

    if (this.unsubscribe) this.unsubscribe();

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      const list = document.getElementById('trainer-leaderboard-list');
      if (!list) return;
      list.innerHTML = '';

      snapshot.forEach((doc, i) => {
        const data = doc.data();
        const item = document.createElement('div');
        item.className = 'flex justify-between items-center bg-zinc-50 border border-zinc-100 px-6 py-5 rounded-2xl';
        item.innerHTML = `
          <div class="flex items-center gap-5">
            <span class="text-4xl text-amber-600 font-bold">${i + 1}</span>
            <div>
              <div class="font-bold text-lg">${data.name}</div>
              <div class="text-xs text-zinc-500">${data.mode?.toUpperCase()} • Level ${data.level}</div>
            </div>
          </div>
          <div class="text-right">
            <div class="text-4xl font-bold text-blue-900">${data.score}</div>
          </div>
        `;
        list.appendChild(item);
      });
    });
  }
}

// Make it globally accessible for inline onclick handlers
window.mathTrainer = null;

// Export a function to easily mount the game
export function initMathBrainTrainer(containerId = 'app') {
  window.mathTrainer = new MathBrainTrainer(containerId);
  return window.mathTrainer;
}